/**
 * Podiatry Demo Seed — canonical IDs and mock OCR payloads.
 *
 * Mirrors the seed inserted by `seed_podiatry_demo` (patients, scheduled_cases,
 * authorization_requests). Used by:
 *   - useTemplateWorkflow.enrichWithPreLoadedDocs → attaches mock docs to the
 *     ScheduledCase so extract-clinical-severity / analyze-pre-auth-risk receive
 *     real text without violating the Zero-Fact Policy.
 *   - reset-podiatry-demo edge function → restores these exact IDs to
 *     `pending_surgeon_approval` and clears downstream state.
 */

export interface PodiatryDemoProfile {
  scheduledCaseId: string;
  authorizationRequestId: string;
  patientId: string;
  patientName: string;
  targetCpt: string;
  modifier: string;
  mockClinicalDocumentText: string;
  /** Short label shown in the pre-loaded docs badge */
  docLabel: string;
}

/**
 * Display-only demo surgeon for the podiatry workflow.
 * The DB FK still points to the placeholder auth user (Larkin's id) so
 * authorization_requests.surgeon_id satisfies the auth.users FK; the UI
 * overrides the displayed name/credentials/practice via this constant.
 */
export const PODIATRY_DEMO_SURGEON = {
  displayName: 'Bryan Fallis, DPM',
  shortName: 'Dr. Bryan Fallis',
  credentials: 'DPM',
  specialty: 'Podiatry',
  practiceName: 'Progressive Podiatry',
} as const;

/** Display-only demo facility for the podiatry workflow. */
export const PODIATRY_DEMO_FACILITY = {
  name: 'Progressive Podiatry',
  type: 'Office',
  address: '2300 Chamber Center Dr Ste 100, Ft Mitchell, KY 41017',
} as const;

export const PODIATRY_DEMO_PROFILES: PodiatryDemoProfile[] = [
  {
    scheduledCaseId: '5ced1ed0-0001-4000-8000-000000000001',
    authorizationRequestId: 'a07e0001-0001-4000-8000-000000000001',
    patientId: 'd0d1a7e0-0001-4000-8000-000000000001',
    patientName: 'Arthur Pendelton',
    targetCpt: '28005',
    modifier: 'RT',
    docLabel: 'MRI + Diabetic Foot Workup',
    mockClinicalDocumentText:
      'Chief Complaint: Diabetic foot ulcer right foot, non-healing for 12 weeks. ' +
      'Imaging: MRI reveals marrow edema and cortical destruction consistent with osteomyelitis ' +
      'of the right 1st metatarsal head. Vitals: HbA1c 8.4%. Vascular: ABI 0.9. ' +
      'Plan: Incision and debridement of bone cortex.',
  },
  {
    scheduledCaseId: '5ced1ed0-0002-4000-8000-000000000002',
    authorizationRequestId: 'a07e0002-0002-4000-8000-000000000002',
    patientId: 'd0d1a7e0-0002-4000-8000-000000000002',
    patientName: 'Maria Gonzalez',
    targetCpt: '15002',
    modifier: 'LT',
    docLabel: 'Wound Assessment + 8wk Conservative Tx',
    mockClinicalDocumentText:
      'Patient presents with chronic venous stasis ulcer on the left lower leg. ' +
      'Failed 8 weeks of compression therapy and Unna boots. ' +
      'Wound assessment: Defect measures exactly 45 sq cm. Subcutaneous tissue is fibrotic. ' +
      'Plan: Surgical excision and preparation of recipient site for future grafting.',
  },
  {
    scheduledCaseId: '5ced1ed0-0003-4000-8000-000000000003',
    authorizationRequestId: 'a07e0003-0003-4000-8000-000000000003',
    patientId: 'd0d1a7e0-0003-4000-8000-000000000003',
    patientName: 'James Carter',
    targetCpt: '27658',
    modifier: 'RT',
    docLabel: 'MRI — Acute FHL Rupture',
    mockClinicalDocumentText:
      '35yo male, acute injury while playing tennis. Sudden pop in right lower leg. ' +
      'MRI confirms complete primary rupture of the right flexor hallucis longus (FHL) tendon, ' +
      'retracted 3cm. Plan: Primary surgical repair of flexor tendon, right leg.',
  },
  {
    scheduledCaseId: '5ced1ed0-0004-4000-8000-000000000004',
    authorizationRequestId: 'a07e0004-0004-4000-8000-000000000004',
    patientId: 'd0d1a7e0-0004-4000-8000-000000000004',
    patientName: 'Sarah Jenkins',
    targetCpt: '27696',
    modifier: 'LT',
    docLabel: 'MRI + 16wk PT Documentation',
    mockClinicalDocumentText:
      'Patient complains of giving way in the left ankle. Has failed 16 weeks of structured ' +
      'physical therapy and bracing. MRI shows complete disruption of the ATFL and partial ' +
      'tearing of the deltoid ligament. Plan: Primary repair of both collateral ligaments, left ankle.',
  },
  {
    scheduledCaseId: '5ced1ed0-0005-4000-8000-000000000005',
    authorizationRequestId: 'a07e0005-0005-4000-8000-000000000005',
    patientId: 'd0d1a7e0-0005-4000-8000-000000000005',
    patientName: 'Robert Vance',
    targetCpt: '28270',
    modifier: 'T6',
    docLabel: 'Conservative Care Failure (6mo)',
    mockClinicalDocumentText:
      'Patient with painful dorsal contracture of the right 2nd toe at the MTP joint. ' +
      'Conservative care including wide-toe box shoes, padding, and custom orthotics ' +
      'failed over the last 6 months. Deformity is rigid. Plan: MTP Capsulotomy, right 2nd digit.',
  },
];

/** Lookup map: scheduled_case.id → profile */
export const PODIATRY_DEMO_BY_SCHEDULED_CASE: Record<string, PodiatryDemoProfile> =
  Object.fromEntries(PODIATRY_DEMO_PROFILES.map((p) => [p.scheduledCaseId, p]));

/** All authorization_request IDs the Reset Demo State action should restore. */
export const PODIATRY_DEMO_AUTHORIZATION_IDS: string[] =
  PODIATRY_DEMO_PROFILES.map((p) => p.authorizationRequestId);
